/**
 * WP15 — czysty model osiedla: mapowanie listy domów (API WP2 §9) na sloty
 * do wyrenderowania w Town. Bez Phasera → testowalny jednostkowo.
 */
import type { HousesListResponse } from '@pieskowo/shared';

type HouseItem = HousesListResponse['houses'][number];

export type EstateBadge = 'owned' | 'member' | 'locked' | 'empty';

export type EstateSlotVM = {
  /** id domu z serwera (null dla placeholderów / offline). */
  houseId: string | null;
  /** Etykieta PL (imię pieska-właściciela lub tekst zastępczy). */
  labelPl: string;
  badge: EstateBadge;
  /** Emoji-badge (zero-reading). */
  badgePl: string;
  /** Czy pokazać duży przycisk „zamieszkaj razem". */
  canRequest: boolean;
  /** Twój domek (wejście do House). */
  isOwn: boolean;
};

export function estateBadgePl(badge: EstateBadge): string {
  switch (badge) {
    case 'owned':
      return '🏠';
    case 'member':
      return '🤝';
    case 'locked':
      return '🔒';
    default:
      return '🏚️';
  }
}

function toVM(h: HouseItem): EstateSlotVM {
  const isOwn = h.badge === 'owned';
  return {
    houseId: h.id,
    labelPl: isOwn ? 'Twój domek' : h.ownerDogName,
    badge: h.badge,
    badgePl: estateBadgePl(h.badge),
    canRequest: h.badge === 'locked' && h.canRequest,
    isOwn,
  };
}

function ownFallback(): EstateSlotVM {
  return {
    houseId: null,
    labelPl: 'Twój domek',
    badge: 'owned',
    badgePl: estateBadgePl('owned'),
    canRequest: false,
    isOwn: true,
  };
}

function emptySlot(): EstateSlotVM {
  return {
    houseId: null,
    labelPl: 'Wolna działka',
    badge: 'empty',
    badgePl: estateBadgePl('empty'),
    canRequest: false,
    isOwn: false,
  };
}

function lockedFallback(): EstateSlotVM {
  return {
    houseId: null,
    labelPl: 'Domek',
    badge: 'locked',
    badgePl: estateBadgePl('locked'),
    canRequest: false,
    isOwn: false,
  };
}

/**
 * Buduje `slotCount` slotów osiedla: slot 0 = Twój domek, kolejne = inni gracze.
 * `null` (offline / błąd API) → Twój domek + zamknięte placeholdery (bez akcji).
 */
export function estateSlotsFromHouses(
  houses: HousesListResponse['houses'] | null,
  slotCount = 3,
): EstateSlotVM[] {
  const slots: EstateSlotVM[] = [];

  if (houses === null) {
    slots.push(ownFallback());
    for (let i = 1; i < slotCount; i++) slots.push(lockedFallback());
    return slots;
  }

  const own = houses.find((h) => h.badge === 'owned') ?? null;
  const others = houses.filter((h) => h.badge !== 'owned');

  slots.push(own ? toVM(own) : ownFallback());
  for (let i = 1; i < slotCount; i++) {
    const h = others[i - 1];
    slots.push(h ? toVM(h) : emptySlot());
  }
  return slots;
}
